/**
 * Ordenación de listados de IPAM por columnas.
 *
 * Lo que hace falta aquí es comparar direcciones, y el orden alfabético de un
 * CIDR miente: "10.0.0.0/8" < "9.0.0.0/8" en texto y al revés en realidad, y
 * "172.16.5.0/24" quedaría detrás de "172.16.10.0/24". Se compara por valor
 * numérico (IPv4 como entero de 32 bits, IPv6 hexteto a hexteto).
 */

function ipv4ToLong(ip) {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let value = 0;
  for (const part of parts) {
    const octet = Number(part);
    if (!Number.isInteger(octet) || octet < 0 || octet > 255) return null;
    value = value * 256 + octet;
  }
  return value;
}

function ipv6ToHextets(ip) {
  const [head, tail] = ip.split('::');
  const headParts = head ? head.split(':').filter(Boolean) : [];
  const tailParts = tail !== undefined ? tail.split(':').filter(Boolean) : [];
  const missing = 8 - headParts.length - tailParts.length;
  if (tail === undefined && headParts.length !== 8) return null;
  if (missing < 0) return null;
  const hextets = [
    ...headParts,
    ...Array(tail === undefined ? 0 : missing).fill('0'),
    ...tailParts,
  ];
  const numbers = hextets.map((h) => parseInt(h, 16));
  return numbers.some(Number.isNaN) ? null : numbers;
}

/** Clave numérica comparable de un CIDR (o de una IP suelta). */
export function cidrSortKey(cidr) {
  const value = String(cidr || '').trim();
  if (!value) return [3, 0, 0]; // los vacíos, al final
  const [address, prefix] = value.split('/');
  const bits = prefix === undefined ? 0 : Number(prefix) || 0;

  if (address.includes(':')) {
    const hextets = ipv6ToHextets(address);
    if (!hextets) return [2, 0, 0];
    return [1, hextets, bits];
  }
  const long = ipv4ToLong(address);
  if (long === null) return [2, 0, 0];
  return [0, long, bits];
}

function compareHextets(a = [], b = []) {
  for (let i = 0; i < 8; i += 1) {
    const left = a[i] ?? 0;
    const right = b[i] ?? 0;
    if (left !== right) return left < right ? -1 : 1;
  }
  return 0;
}

/** Comparador para cadenas tipo CIDR/IP (IPv4 primero, luego IPv6). */
export function compareCidr(a, b) {
  const [familyA, valueA, prefixA] = cidrSortKey(a);
  const [familyB, valueB, prefixB] = cidrSortKey(b);
  if (familyA !== familyB) return familyA - familyB;
  const byAddress = Array.isArray(valueA) || Array.isArray(valueB)
    ? compareHextets(Array.isArray(valueA) ? valueA : [], Array.isArray(valueB) ? valueB : [])
    : (valueA === valueB ? 0 : valueA < valueB ? -1 : 1);
  if (byAddress !== 0) return byAddress;
  return prefixA - prefixB;
}

/** Comparador de texto (insensible a mayúsculas y a acentos). */
export function compareText(a, b) {
  const left = String(a ?? '').trim();
  const right = String(b ?? '').trim();
  if (!left && !right) return 0;
  if (!left) return 1; // los vacíos, al final
  if (!right) return -1;
  return left.localeCompare(right, 'es', { sensitivity: 'base', numeric: true });
}

/** Comparador numérico (los vacíos, al final). */
export function compareNumber(a, b) {
  const left = a === null || a === undefined || a === '' ? null : Number(a);
  const right = b === null || b === undefined || b === '' ? null : Number(b);
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
}

/**
 * Ordena una copia de `rows` por `{ key, dir }` usando `accessors` (mapa
 * clave → función que devuelve el valor comparable).
 */
export function sortRows(rows, sort, accessors) {
  if (!sort?.key || !accessors?.[sort.key]) return [...rows];
  const getValue = accessors[sort.key];
  const factor = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => factor * getValue(a, b));
}

/** Grupos por sitio, con «Sin site» al final y orden natural por nombre. */
export function groupBySite(rows, getSite = (row) => row.site) {
  const groups = new Map();
  rows.forEach((row) => {
    const site = (getSite(row) || '').trim();
    const key = site || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : compareText(a, b)))
    .map(([site, groupRows]) => ({ site, rows: groupRows }));
}
